/**
 * Store IndexedDB pour les blobs (photos + voice messages).
 *
 * Phase 2, incrément 2.1. API minimale — pas de quota check, pas de
 * GC automatique. La gestion du cycle de vie est laissée à l'appelant
 * (ex. `removeBlob` quand on supprime une photo).
 *
 * Convention de clés :
 *   - `photo:<photoId>` pour les images uploadées.
 *   - `voice:<messageId>` pour les voice messages.
 *   - Autres préfixes possibles plus tard (album thumbnail, etc.).
 *
 * Tous les helpers sont **gracieux côté erreur** : retournent `null`
 * ou `false` plutôt que de throw. L'app continue de fonctionner sans
 * IDB (mode dégradé Safari privacy).
 */
import { openDb, type BlobEntry } from './db.js';

/**
 * Enregistre un blob sous la clé donnée. Écrase l'éventuelle valeur
 * précédente. Sérialise via `ArrayBuffer` (cf. commentaire `BlobEntry`
 * dans `db.ts`). Retourne `true` si la persistance a réussi.
 */
export async function putBlob(key: string, blob: Blob): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  try {
    const data = await blob.arrayBuffer();
    const entry: BlobEntry = {
      key,
      data,
      contentType: blob.type || 'application/octet-stream',
      size: blob.size,
      addedAt: Date.now(),
    };
    await db.put('blobs', entry);
    return true;
  } catch (err) {
    console.warn(`[storage:blobs] putBlob(${key}) failed:`, err);
    return false;
  }
}

/**
 * Récupère un blob par clé. Retourne `null` si absent, ou si IDB
 * indisponible, ou en cas d'erreur. Le Blob retourné est reconstruit
 * depuis l'ArrayBuffer + contentType stockés (instance fraîche).
 */
export async function getBlob(key: string): Promise<Blob | null> {
  const entry = await getBlobEntry(key);
  if (!entry) return null;
  return new Blob([entry.data], { type: entry.contentType });
}

/**
 * Récupère l'entrée brute (ArrayBuffer + métadonnées). Utile pour
 * connaître le contentType / size sans reconstruire le Blob.
 */
export async function getBlobEntry(key: string): Promise<BlobEntry | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    return (await db.get('blobs', key)) ?? null;
  } catch (err) {
    console.warn(`[storage:blobs] getBlobEntry(${key}) failed:`, err);
    return null;
  }
}

/**
 * Supprime un blob par clé. Retourne `true` si la suppression a
 * abouti (ou si la clé n'existait pas — idempotent).
 */
export async function deleteBlob(key: string): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  try {
    await db.delete('blobs', key);
    return true;
  } catch (err) {
    console.warn(`[storage:blobs] deleteBlob(${key}) failed:`, err);
    return false;
  }
}

/**
 * Liste toutes les clés présentes (utilitaire debug + futur GC
 * automatique). Pagination = pas en Phase 2.
 */
export async function listBlobKeys(): Promise<string[]> {
  const db = await openDb();
  if (!db) return [];
  try {
    return await db.getAllKeys('blobs');
  } catch (err) {
    console.warn('[storage:blobs] listBlobKeys failed:', err);
    return [];
  }
}

/**
 * Construit une URL object éphémère pour afficher un blob dans un
 * `<img src>` / `<audio src>`. **L'appelant doit appeler
 * `URL.revokeObjectURL(url)` quand l'élément est démonté** sinon
 * fuite mémoire.
 */
export async function getBlobUrl(key: string): Promise<string | null> {
  const blob = await getBlob(key);
  if (!blob) return null;
  return URL.createObjectURL(blob);
}
